import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import GalleryLightbox from '../components/GalleryLightbox';
import { COMPANY } from '../data/seedData';
import { imageUrl } from '../lib/images';

export default function AboutPage() {
  return (
    <>
      <SeoHead
        title="Om RB Glasfiber - RB Glasfiber A/S"
        description="RB Glasfiber er specialist i udførsel og reparation af glasfiberopgaver. Vi fremstiller pumpebrønde, tanke, containere og specialopgaver."
        canonical="https://www.rb-glasfiber.dk/rb-glasfiber"
        ogImage="https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/formbygning-1-scaled-e1582012516853-cropresize-1408x704.webp"
      />

      {/* Hero Header */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 bg-slate-950 text-white overflow-hidden border-b border-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/formbygning-1-scaled-e1582012516853-cropresize-1408x704.webp', 'width=1600,quality=80,format=auto')}
            alt="Om RB Glasfiber"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#1e9457]/20 border border-[#1e9457]/40 text-[#22c55e] text-xs font-bold uppercase tracking-widest mb-4">
              <i className="fa-solid fa-industry"></i>
              <span>Dansk glasfiber produktion</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-white mb-6">
              Om RB Glasfiber
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
              RB Glasfiber er specialist i udførsel og reparation af glasfiberopgaver. Vi leverer skræddersyede løsninger til forsyning, industri, kommuner og private.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 uppercase">
                  Specialist i glasfiber og komposit
                </h2>
                <div className="w-12 h-1 bg-[#1e9457] mt-3"></div>
              </div>

              <div className="prose text-slate-700 text-base leading-relaxed space-y-4">
                <p>
                  RB Glasfiber er specialist i udførsel af glasfiberopgaver. Vi laver bl.a. containere, batteribokse, pumpebrønde og -tanke og har udviklet et roterende overdæknings-koncept til minimering af lugtgener ved renseanlæg.
                </p>
                <p>
                  Derudover står vi altid klar til at hjælpe med reparationer i glasfiber. Uanset, hvor stor eller kompleks reparationsopgaven er, er du altid velkommen til at kontakte os for at høre nærmere.
                </p>
                <p>
                  Vi arbejder med miljøet for øje, og en lang række af vores produkter er CE godkendte. Har du spørgsmål til produktionen, materialevalg eller en konkret opgave, så kontakt os.
                </p>
                <p>
                  Vi tror på den gode dialog. Kontakt os på{' '}
                  <a href={`mailto:${COMPANY.email}`} className="text-[#1e9457] font-semibold underline whitespace-nowrap">
                    {COMPANY.email}
                  </a>{' '}
                  eller ring på{' '}
                  <a href={`tel:${COMPANY.phoneRaw}`} className="text-[#1e9457] font-semibold underline whitespace-nowrap">
                    {COMPANY.phone}
                  </a>
                  .
                </p>
              </div>

              {/* Badges / Values */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <i className="fa-solid fa-certificate text-2xl text-[#1e9457] mb-2"></i>
                  <h4 className="font-bold text-slate-900 text-sm">CE Godkendt</h4>
                  <p className="text-xs text-slate-600 mt-1">En lang række af vores produkter er CE godkendte.</p>
                </div>

                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <i className="fa-solid fa-leaf text-2xl text-[#1e9457] mb-2"></i>
                  <h4 className="font-bold text-slate-900 text-sm">Miljøbevidste</h4>
                  <p className="text-xs text-slate-600 mt-1">Vi arbejder med miljøet og holdbarhed for øje.</p>
                </div>

                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <i className="fa-solid fa-truck-fast text-2xl text-[#1e9457] mb-2"></i>
                  <h4 className="font-bold text-slate-900 text-sm">Udkørende</h4>
                  <p className="text-xs text-slate-600 mt-1">Reparationsarbejde udføres både på værksted og on-site.</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-6">
              <div className="rounded-xl overflow-hidden border border-slate-200 shadow-md aspect-[4/3] bg-slate-900">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/formbygning-1-scaled-e1582012516853-cropresize-1408x704.webp', 'width=700,quality=85,format=auto')}
                  alt="Værksted og formbygning"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Company Info Box */}
              <div className="p-6 bg-slate-900 text-slate-300 rounded-xl border border-slate-800 space-y-3">
                <h3 className="text-white font-bold uppercase tracking-wider text-xs flex items-center gap-2">
                  <i className="fa-solid fa-building text-[#1e9457]"></i>
                  Virksomhedsoplysninger
                </h3>
                <div className="text-sm space-y-2">
                  <div>Firmanavn: <strong className="text-white">{COMPANY.name}</strong></div>
                  <div>CVR: <strong className="text-white font-mono">{COMPANY.cvr}</strong></div>
                  <div>Adresse: <span className="text-white">{COMPANY.address.full}</span></div>
                  <div>Telefon: <a href={`tel:${COMPANY.phoneRaw}`} className="text-white font-semibold hover:underline whitespace-nowrap">{COMPANY.phone}</a></div>
                  <div>E-mail: <a href={`mailto:${COMPANY.email}`} className="text-white font-semibold hover:underline whitespace-nowrap">{COMPANY.email}</a></div>
                  <div>Daglig ledelse: <span className="text-white">{COMPANY.contactPerson}</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Underliggende sider / Ydelser */}
          <div className="pt-12 border-t border-slate-200">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-[#1e9457] uppercase tracking-wider mb-2">
                <span className="w-2 h-2 rounded-full bg-[#1e9457]"></span>
                Vores specialer & produkter
              </div>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 uppercase">
                RB Glasfiber arbejdsområder
              </h3>
              <p className="text-slate-600 text-sm mt-1">
                Læs mere om vores fire centrale produkt- og ydelsesområder inden for glasfiberkonstruktioner.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link
                to="/rb-glasfiber/pumpebronde-amp-tanke"
                className="group flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs hover:shadow-md hover:border-[#1e9457]/50 transition-all"
              >
                <div className="aspect-[4/3] bg-slate-900 overflow-hidden">
                  <img
                    src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/rb-glasfiber-pumpebroend-glasfiber-thumb1-cropresize-550x413.webp', 'width=500,quality=80,format=auto')}
                    alt="Pumpebrønde & tanke"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
                  <div>
                    <h4 className="font-bold text-slate-900 text-base group-hover:text-[#1e9457] transition-colors">
                      Pumpebrønde & tanke
                    </h4>
                    <p className="text-xs text-slate-600 mt-1 line-clamp-3">
                      Pumpebrønde, indstøbningsbrønde, dobbeltvæggede tanke og brøndtoppe fremstillet i kemikalieresistent vinylester.
                    </p>
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1e9457] uppercase tracking-wider pt-2 border-t border-slate-100">
                    <span>Læs mere</span>
                    <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </div>
              </Link>

              <Link
                to="/rb-glasfiber/reparationer"
                className="group flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs hover:shadow-md hover:border-[#1e9457]/50 transition-all"
              >
                <div className="aspect-[4/3] bg-slate-900 overflow-hidden">
                  <img
                    src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/rep-faerch-plast-scaled-e1581422895121-cropresize-412x619.webp', 'width=500,quality=80,format=auto')}
                    alt="Reparationer"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
                  <div>
                    <h4 className="font-bold text-slate-900 text-base group-hover:text-[#1e9457] transition-colors">
                      Reparationer
                    </h4>
                    <p className="text-xs text-slate-600 mt-1 line-clamp-3">
                      Udkørende og fleksibel service. Vi reparerer siloer, tanke, procesanlæg og både i glasfiber på hele Sjælland, Fyn og Jylland.
                    </p>
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1e9457] uppercase tracking-wider pt-2 border-t border-slate-100">
                    <span>Læs mere</span>
                    <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </div>
              </Link>

              <Link
                to="/rb-glasfiber/overdaekning"
                className="group flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs hover:shadow-md hover:border-[#1e9457]/50 transition-all"
              >
                <div className="aspect-[4/3] bg-slate-900 overflow-hidden">
                  <img
                    src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/oversaekning-cropresize-1408x704.webp', 'width=500,quality=80,format=auto')}
                    alt="Overdækning"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
                  <div>
                    <h4 className="font-bold text-slate-900 text-base group-hover:text-[#1e9457] transition-colors">
                      Overdækning
                    </h4>
                    <p className="text-xs text-slate-600 mt-1 line-clamp-3">
                      Patenteret roterende overdækningskoncept til minimering af lugtgener ved renseanlæg og procestanke for industri og forsyning.
                    </p>
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1e9457] uppercase tracking-wider pt-2 border-t border-slate-100">
                    <span>Læs mere</span>
                    <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </div>
              </Link>

              <Link
                to="/rb-glasfiber/specialopgaver"
                className="group flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs hover:shadow-md hover:border-[#1e9457]/50 transition-all"
              >
                <div className="aspect-[4/3] bg-slate-900 overflow-hidden">
                  <img
                    src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/trappe-1-cropresize-1408x704.webp', 'width=500,quality=80,format=auto')}
                    alt="Specialopgaver"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
                  <div>
                    <h4 className="font-bold text-slate-900 text-base group-hover:text-[#1e9457] transition-colors">
                      Specialopgaver
                    </h4>
                    <p className="text-xs text-slate-600 mt-1 line-clamp-3">
                      Formbygning, håndoplæg, foliestøbning og prototyper i glasfiber, stål, træ og plast efter specifikke kundekrav.
                    </p>
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1e9457] uppercase tracking-wider pt-2 border-t border-slate-100">
                    <span>Læs mere</span>
                    <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </div>
              </Link>
            </div>
          </div>

          {/* Lightbox Gallery */}
          <div className="pt-8 border-t border-slate-200">
            <GalleryLightbox
              gallerySlug="specialopgaver"
              title="Fotogalleri: Produktion og opgaver"
              description="Billeder fra vores produktion, forme og udførte specialløsninger."
              columns={3}
            />
          </div>
        </div>
      </section>
    </>
  );
}
